import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Grid,
    Card,
    CardContent,
    Divider,
    CircularProgress,
    Button,
    useTheme
} from '@mui/material';
import {
    Notifications as NotificationsIcon,
    NotificationsActive as UnreadIcon,
    People as UsersIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip as ChartTooltip,
    Legend,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    BarElement
} from 'chart.js';
import { Pie, Line, Bar } from 'react-chartjs-2';
import api from '../../services/api';

// Enregistrer les composants Chart.js
ChartJS.register(
    ArcElement,
    ChartTooltip,
    Legend,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title
);

/**
 * Composant pour afficher les statistiques des notifications
 */
const NotificationStats = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const theme = useTheme();

    // Charger les statistiques au chargement du composant
    useEffect(() => {
        fetchStats();
    }, []);

    /**
     * Récupérer les statistiques des notifications
     */
    const fetchStats = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await api.get('/admin/notifications/stats');

            if (response.data.success) {
                setStats(response.data.stats);
            } else {
                setError('Erreur lors de la récupération des statistiques');
            }
        } catch (err) {
            console.error('Error fetching notification stats:', err);
            setError(err.response?.data?.message || 'Erreur lors de la récupération des statistiques');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Préparer les données pour le graphique en camembert des types de notifications
     */
    const prepareTypeChartData = () => {
        if (!stats || !stats.notificationsByType) return null;

        const labels = [];
        const data = [];
        const backgroundColor = [];
        const borderColor = [];

        stats.notificationsByType.forEach(item => {
            labels.push(getTypeLabel(item.type));
            data.push(parseInt(item.count));

            const colors = getTypeColors(item.type);
            backgroundColor.push(colors.bg);
            borderColor.push(colors.border);
        });

        return {
            labels,
            datasets: [
                {
                    data,
                    backgroundColor,
                    borderColor,
                    borderWidth: 1
                }
            ]
        };
    };

    /**
     * Préparer les données pour le graphique en camembert des catégories de notifications
     */
    const prepareCategoryChartData = () => {
        if (!stats || !stats.notificationsByCategory) return null;

        const labels = [];
        const data = [];
        const backgroundColor = [
            'rgba(54, 162, 235, 0.6)',
            'rgba(255, 99, 132, 0.6)',
            'rgba(255, 206, 86, 0.6)',
            'rgba(75, 192, 192, 0.6)',
            'rgba(153, 102, 255, 0.6)'
        ];
        const borderColor = [
            'rgba(54, 162, 235, 1)',
            'rgba(255, 99, 132, 1)',
            'rgba(255, 206, 86, 1)',
            'rgba(75, 192, 192, 1)',
            'rgba(153, 102, 255, 1)'
        ];

        stats.notificationsByCategory.forEach((item, index) => {
            labels.push(getCategoryLabel(item.category));
            data.push(parseInt(item.count));
        });

        return {
            labels,
            datasets: [
                {
                    data,
                    backgroundColor,
                    borderColor,
                    borderWidth: 1
                }
            ]
        };
    };

    /**
     * Préparer les données pour le graphique en ligne des notifications récentes
     */
    const prepareRecentChartData = () => {
        if (!stats || !stats.recentNotifications) return null;

        const labels = [];
        const data = [];

        // Trier les données par date
        const sortedData = [...stats.recentNotifications].sort((a, b) =>
            new Date(a.date) - new Date(b.date)
        );

        sortedData.forEach(item => {
            // Formater la date (YYYY-MM-DD -> DD/MM)
            const date = new Date(item.date);
            const formattedDate = `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}`;

            labels.push(formattedDate);
            data.push(parseInt(item.count));
        });

        return {
            labels,
            datasets: [
                {
                    label: 'Notifications envoyées',
                    data,
                    borderColor: theme.palette.primary.main,
                    backgroundColor: theme.palette.primary.light + '80',
                    tension: 0.3,
                    fill: true
                }
            ]
        };
    };

    /**
     * Obtenir le libellé d'un type de notification
     */
    const getTypeLabel = (type) => {
        switch (type) {
            case 'info':
                return 'Information';
            case 'success':
                return 'Succès';
            case 'warning':
                return 'Avertissement';
            case 'error':
                return 'Erreur';
            default:
                return type;
        }
    };

    /**
     * Obtenir les couleurs d'un type de notification
     */
    const getTypeColors = (type) => {
        switch (type) {
            case 'info':
                return {
                    bg: 'rgba(33, 150, 243, 0.6)',
                    border: 'rgba(33, 150, 243, 1)'
                };
            case 'success':
                return {
                    bg: 'rgba(76, 175, 80, 0.6)',
                    border: 'rgba(76, 175, 80, 1)'
                };
            case 'warning':
                return {
                    bg: 'rgba(255, 152, 0, 0.6)',
                    border: 'rgba(255, 152, 0, 1)'
                };
            case 'error':
                return {
                    bg: 'rgba(244, 67, 54, 0.6)',
                    border: 'rgba(244, 67, 54, 1)'
                };
            default:
                return {
                    bg: 'rgba(158, 158, 158, 0.6)',
                    border: 'rgba(158, 158, 158, 1)'
                };
        }
    };

    /**
     * Obtenir le libellé d'une catégorie de notification
     */
    const getCategoryLabel = (category) => {
        switch (category) {
            case 'system':
                return 'Système';
            case 'account':
                return 'Compte';
            case 'security':
                return 'Sécurité';
            case 'properties':
                return 'Propriétés';
            case 'messages':
                return 'Messages';
            default:
                return category;
        }
    };

    // Options communes pour les graphiques en camembert
    const pieOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom'
            }
        }
    };

    // Options pour le graphique en ligne
    const lineOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top'
            },
            title: {
                display: true,
                text: 'Notifications des 7 derniers jours'
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    precision: 0
                }
            }
        }
    };

    // Préparer les données des graphiques
    const typeChartData = prepareTypeChartData();
    const categoryChartData = prepareCategoryChartData();
    const recentChartData = prepareRecentChartData();

    return (
        <Paper sx={{ p: 3, mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Statistiques des notifications</Typography>

                <Button
                    startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
                    onClick={fetchStats}
                    disabled={loading}
                >
                    Actualiser
                </Button>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography color="error">{error}</Typography>
                    <Button
                        variant="contained"
                        sx={{ mt: 2 }}
                        onClick={fetchStats}
                    >
                        Réessayer
                    </Button>
                </Box>
            ) : stats ? (
                <>
                    {/* Cartes de statistiques */}
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        <Grid item xs={12} sm={4}>
                            <Card>
                                <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                                    <NotificationsIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                                    <Box>
                                        <Typography variant="h4">{stats.totalNotifications}</Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            Notifications totales
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <Card>
                                <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                                    <UnreadIcon sx={{ fontSize: 40, color: 'error.main', mr: 2 }} />
                                    <Box>
                                        <Typography variant="h4">{stats.totalUnread}</Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            Notifications non lues
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <Card>
                                <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                                    <UsersIcon sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
                                    <Box>
                                        <Typography variant="h4">{stats.totalUsers}</Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            Utilisateurs
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Graphiques */}
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Card>
                                <CardContent>
                                    <Typography variant="subtitle1" gutterBottom>
                                        Notifications par type
                                    </Typography>
                                    <Box sx={{ height: 300 }}>
                                        {typeChartData ? (
                                            <Pie data={typeChartData} options={pieOptions} />
                                        ) : (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                                <Typography color="textSecondary">Aucune donnée disponible</Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Card>
                                <CardContent>
                                    <Typography variant="subtitle1" gutterBottom>
                                        Notifications par catégorie
                                    </Typography>
                                    <Box sx={{ height: 300 }}>
                                        {categoryChartData ? (
                                            <Pie data={categoryChartData} options={pieOptions} />
                                        ) : (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                                <Typography color="textSecondary">Aucune donnée disponible</Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12}>
                            <Card>
                                <CardContent>
                                    <Typography variant="subtitle1" gutterBottom>
                                        Activité récente
                                    </Typography>
                                    <Box sx={{ height: 300 }}>
                                        {recentChartData ? (
                                            <Line data={recentChartData} options={lineOptions} />
                                        ) : (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                                <Typography color="textSecondary">Aucune donnée disponible</Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </>
            ) : null}
        </Paper>
    );
};

export default NotificationStats;